import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Send, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

interface ShareReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  shareCode: string;
}

export function ShareReportModal({ isOpen, onClose, shareCode }: ShareReportModalProps) {
  useLanguage();
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async () => {
    if (!description.trim()) return;
    
    setIsSubmitting(true);
    
    // Mock submission
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    console.log('[Report] Submitted:', { shareCode, description });
    
    setIsSubmitting(false);
    setIsSubmitted(true);
    
    toast.success(translateInterfaceText("Relatório enviado com sucesso"));
    
    // Auto-close after showing success
    setTimeout(() => {
      onClose();
      setDescription('');
      setIsSubmitted(false);
    }, 1500);
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
      setDescription('');
      setIsSubmitted(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-[400px] p-0 gap-0 rounded-[16px] overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.12)]">
        {/* Content */}
        <DialogHeader className="p-6 pb-4 border-b border-border">
          <DialogTitle className="text-lg font-bold text-foreground">
            {translateInterfaceText("Reportar problema")}</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {translateInterfaceText("Descreva o problema que você encontrou com este compartilhamento.")}</DialogDescription>
        </DialogHeader>

        <div className="p-6">
          {isSubmitted ? (
            <div className="text-center py-4">
              <div className="w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-3">
                <CheckCircle2 className="h-6 w-6 text-emerald-500" />
              </div>
              <p className="text-sm text-muted-foreground">
                {translateInterfaceText("Obrigado por nos informar!")}</p>
            </div>
          ) : (
            <Textarea
              placeholder={translateInterfaceText("Descreva o problema...")}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="min-h-[100px] resize-none"
              disabled={isSubmitting}
            />
          )}
        </div>

        {/* Footer */}
        {!isSubmitted && (
          <DialogFooter className="p-6 pt-0">
            <div className="flex gap-2 w-full">
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
                className="flex-1"
              >
                {translateInterfaceText("Cancelar")}</Button>
              <Button
                onClick={handleSubmit}
                disabled={!description.trim() || isSubmitting}
                className="flex-1"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    {translateInterfaceText("Enviando…")}</>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    {translateInterfaceText("Enviar")}</>
                )}
              </Button>
            </div>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
